import React from 'react';
import { ArrowLeft, Clock, Sparkles, Bell, Mail, Package, Zap, Heart, Star, Rocket } from 'lucide-react';

interface ComingSoonProps {
  title?: string;
  subtitle?: string;
  description?: string;
  onBack?: () => void;
  showNotifyButton?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  estimatedLaunch?: string;
}

export default function ComingSoon({ 
  title = "Coming Soon",
  subtitle = "We're cooking up something amazing",
  description = "This feature is currently in development. We're working hard to bring you the best possible experience.",
  onBack,
  showNotifyButton = true,
  icon: Icon = Rocket,
  estimatedLaunch = "Q2 2025"
}: ComingSoonProps) {
  return (
    <>
      <style jsx>{`
        .font-inter { font-family: 'Inter', sans-serif; }
        .font-poppins { font-family: 'Poppins', sans-serif; }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { 
            box-shadow: 0 0 40px rgba(236, 72, 153, 0.4);
          }
          50% { 
            box-shadow: 0 0 60px rgba(236, 72, 153, 0.8);
          }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        .animate-slide-up { animation: slideUp 0.8s ease-out forwards; }
        
        .glass-morphism {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.12);
        }
        
        .premium-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
          backdrop-filter: blur(30px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #ec4899, #8b5cf6, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .shimmer-effect {
          position: relative;
          overflow: hidden;
        }
        
        .shimmer-effect::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          animation: shimmer 3s infinite;
        }
        
        .hover-lift:hover {
          transform: translateY(-4px);
        }
      `}</style>

      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-2 h-2 sm:w-3 sm:h-3 bg-pink-400 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-3 h-3 sm:w-4 sm:h-4 bg-purple-400 rounded-full animate-bounce"></div>
          <div className="absolute bottom-40 left-20 w-1 h-1 sm:w-2 sm:h-2 bg-cyan-400 rounded-full animate-ping"></div>
          <div className="absolute top-1/3 right-1/3 w-4 h-4 sm:w-6 sm:h-6 bg-gradient-to-r from-pink-400/30 to-purple-400/30 rounded-full blur-sm animate-pulse"></div>
        </div>

        {/* Back Button */}
        {onBack && (
          <button
            onClick={onBack}
            className="fixed top-6 left-6 z-50 glass-morphism text-pink-400 px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium hover:bg-pink-500/10 transition-all duration-300 flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
            <span>Back</span>
          </button>
        )}

        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Main Content */}
            <div className="premium-card rounded-2xl sm:rounded-3xl p-8 sm:p-12 lg:p-16 shimmer-effect animate-slide-up">
              {/* Icon */}
              <div className="relative mb-8 sm:mb-12">
                <div className="w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto animate-pulse-glow">
                  <Icon className="h-12 w-12 sm:h-16 sm:w-16 lg:h-20 lg:w-20 text-white" />
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 sm:-top-6 sm:-right-6 w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center animate-float" style={{ animationDelay: '1s' }}>
                  <Sparkles className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                </div>
                
                <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center animate-float" style={{ animationDelay: '2s' }}>
                  <Star className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>

              {/* Title */}
              <h1 className="font-poppins font-black text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white mb-4 sm:mb-6">
                {title}
              </h1>

              {/* Subtitle */}
              <h2 className="font-poppins font-bold text-xl sm:text-2xl lg:text-3xl mb-6 sm:mb-8">
                <span className="gradient-text">{subtitle}</span>
              </h2>

              {/* Description */}
              <p className="font-inter text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl mx-auto mb-8 sm:mb-12">
                {description}
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
                {[
                  {
                    icon: Zap,
                    title: 'Lightning Fast',
                    desc: 'Optimized for speed and performance',
                    color: 'from-yellow-500 to-orange-500'
                  },
                  {
                    icon: Heart,
                    title: 'User-Focused',
                    desc: 'Designed with your needs in mind',
                    color: 'from-pink-500 to-rose-500'
                  },
                  {
                    icon: Package,
                    title: 'Feature-Rich',
                    desc: 'Packed with powerful capabilities',
                    color: 'from-purple-500 to-indigo-500'
                  }
                ].map((feature, index) => (
                  <div key={index} className="glass-morphism rounded-xl sm:rounded-2xl p-4 sm:p-6 hover-lift transition-all duration-300">
                    <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4`}>
                      <feature.icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                    </div>
                    <h3 className="font-poppins font-semibold text-white text-base sm:text-lg mb-2">{feature.title}</h3>
                    <p className="font-inter text-slate-400 text-xs sm:text-sm">{feature.desc}</p>
                  </div>
                ))}
              </div>

              {/* Launch Timeline */}
              <div className="glass-morphism rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-8 sm:mb-12">
                <div className="flex items-center justify-center space-x-3 sm:space-x-4 mb-3 sm:mb-4">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-400" />
                  <span className="font-poppins font-semibold text-white text-base sm:text-lg">Estimated Launch</span>
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-cyan-400 mb-2">{estimatedLaunch}</div>
                <p className="text-slate-400 text-sm sm:text-base">We're working around the clock to bring this to you</p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
                {showNotifyButton && (
                  <button className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 font-poppins font-semibold text-base sm:text-lg hover:shadow-2xl hover:shadow-pink-500/25 transition-all duration-300 hover:scale-105 rounded-xl sm:rounded-2xl flex items-center space-x-2 sm:space-x-3">
                    <Bell className="h-5 w-5 sm:h-6 sm:w-6" />
                    <span>Notify Me When Ready</span>
                  </button>
                )}
                
                {onBack && (
                  <button 
                    onClick={onBack}
                    className="border-2 border-pink-500/50 text-pink-300 px-6 sm:px-8 py-3 sm:py-4 font-poppins font-semibold text-base sm:text-lg hover:bg-pink-500/10 transition-all duration-300 rounded-xl sm:rounded-2xl flex items-center space-x-2 sm:space-x-3"
                  >
                    <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                    <span>Go Back</span>
                  </button>
                )}
              </div>

              {/* Progress Indicator */}
              <div className="mt-8 sm:mt-12">
                <div className="flex items-center justify-between text-slate-400 text-xs sm:text-sm mb-2">
                  <span>Development Progress</span>
                  <span>75%</span>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2 sm:h-3">
                  <div className="bg-gradient-to-r from-pink-500 to-purple-600 h-2 sm:h-3 rounded-full transition-all duration-1000" style={{ width: '75%' }}></div>
                </div>
              </div>
            </div>

            {/* Bottom Message */}
            <div className="mt-8 sm:mt-12 text-center">
              <p className="font-inter text-slate-400 text-sm sm:text-base">
                Want to stay updated? Follow our progress and get notified when we launch.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}